import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, Alert, Spin, Select } from 'antd';
import { MailOutlined, LockOutlined, UserOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext'; // Use auth context for signup function
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const SignupContainer = styled.div`
  // Reusing login styles might be good, or create specific ones
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f0f2f5;
  padding: 20px; // Add padding for smaller screens
`;

const StyledCard = styled(Card)`
  width: 450px; // Slightly wider for more fields
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  @media (max-width: 500px) {
    width: 100%; // Full width on small screens
  }
`;

const { Title } = Typography;
const { Option } = Select;

const SignupPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { signupUser } = useAuth(); // Get signup function from context
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // Roles available for signup (controlled by admin)
  const availableRoles = ['manager', 'viewer']; // Admin can select role

  const onFinish = async (values) => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
       // Ensure password confirmation matches if you add it
        if (values.password !== values.confirmPassword) {
            throw new Error("Passwords do not match!");
        }
      // Call the signup function from AuthContext (mock service)
      await signupUser({
        name: values.name,
        email: values.email,
        password: values.password,
        role: values.role,
      });
      setSuccess('User created successfully! You can now manage users.');
      form.resetFields(); // Clear form on success
      // Decide navigation: maybe stay here or go to user list?
      // navigate('/users'); // Optional: navigate after success
    } catch (err) {
      setError(err.message || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <SignupContainer>
      <StyledCard>
        <Title level={2} style={{ textAlign: 'center', marginBottom: '24px' }}>
          Create New User
        </Title>
        <Form
          form={form}
          name="admin_signup"
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
        >
          {error && <Alert message={error} type="error" showIcon closable style={{ marginBottom: '20px' }} onClose={() => setError('')} />}
          {success && <Alert message={success} type="success" showIcon style={{ marginBottom: '20px' }} />}

          <Form.Item
            name="name"
            label="Full Name"
            rules={[{ required: true, message: 'Please input the full name!' }]}
          >
            <Input prefix={<UserOutlined />} placeholder="Full Name" size="large" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email Address"
            rules={[{ required: true, message: 'Please input the Email!', type: 'email' }]}
          >
            <Input prefix={<MailOutlined />} placeholder="Email" size="large" />
          </Form.Item>

           <Form.Item
                name="role"
                label="User Role"
                rules={[{ required: true, message: 'Please select a role for the user' }]}
                initialValue="viewer" // Default role suggestion
            >
                <Select prefix={<SafetyCertificateOutlined />} placeholder="Select Role" size="large">
                {availableRoles.map(role => (
                    <Option key={role} value={role} style={{ textTransform: 'capitalize'}}>
                       <SafetyCertificateOutlined style={{ marginRight: 8 }} /> {role}
                    </Option>
                ))}
                </Select>
            </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: 'Please input a Password!' }, { min: 6, message: 'Password must be at least 6 characters'}]}
            hasFeedback // Adds icon feedback on validation
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Password" size="large" />
          </Form.Item>

           <Form.Item
                name="confirmPassword"
                label="Confirm Password"
                dependencies={['password']} // Depends on password field
                hasFeedback
                rules={[
                { required: true, message: 'Please confirm your Password!' },
                ({ getFieldValue }) => ({
                    validator(_, value) { // Custom validator
                    if (!value || getFieldValue('password') === value) {
                        return Promise.resolve(); // Validation passes
                    }
                    return Promise.reject(new Error('The two passwords that you entered do not match!')); // Validation fails
                    },
                }),
                ]}
            >
                <Input.Password prefix={<LockOutlined />} placeholder="Confirm Password" size="large" />
            </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block size="large" loading={loading}>
              {loading ? <Spin /> : 'Create User'}
            </Button>
          </Form.Item>
          {/* Maybe add a button to go back to user list or dashboard */}
          <Form.Item style={{ textAlign: 'center' }}>
             <Button type="link" onClick={() => navigate('/users')}>
                Back to User List
             </Button>
          </Form.Item>
        </Form>
      </StyledCard>
    </SignupContainer>
  );
};

export default SignupPage;